import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Link } from "@/libraries/design/components";
import { LinkProps } from "@/libraries/design/components/link/src/link";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

interface Resource {
    heading: string;
    description: string;
    link: LinkProps;
}

const backgroundStyles = css({
    "backgroundImage": "linear-gradient(225deg, #998DD9 0%, #EAE6FF 100%)",
    "backgroundRepeat": "no-repeat",
    "backgroundSize": "auto",
    "width": "100%",
    "padding": "20px 16px 48px",
    [media.above.sm]: {
        padding: "48px 16px 64px"
    },
    // Override the default padding of the row layout
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        padding: "auto 0 auto !important",
        margin: "auto !important"
    }
});

const h2Styles = css({
    color: "#253858 !important",
    fontSize: "1.5rem !important",
    fontWeight: "500 !important",
    lineHeight: "1.3333",
    textAlign: "left !important",
    margin: "0px auto 0px !important",
    padding: "16px 0px 0px",
    width: "100%",
    [media.above.sm]: {
        padding: "12px 16px",
        fontSize: "2.25rem !important",
        textAlign: "center !important"
    }
});

const h4Styles = css({
    color: "#253858 !important",
    fontWeight: "500 !important",
    lineHeight: "1.2",
    marginBlockEnd: "1.33em",
    fontSize: "1rem !important",
    letterSpacing: "0.3px !important",
    [media.above.sm]: {
        fontSize: "1.25rem !important"
    }
});

const pStyles = css({
    color: "#292a2e !important",
    fontSize: "1rem",
    fontWeight: "400 !important",
    lineHeight: "1.5rem",
    marginBlockEnd: "1em"
});

const linkStyles = css({
    transition: "color 0.2s"
});

export const Resources = () => {
    const intl = useIntl();
    const resources: Resource[] = [
        {
            heading: intl.formatMessage({
                id: "jsm.templates.resources.gettingStartedGuide",
                defaultMessage: "Getting started guide"
            }),
            description: intl.formatMessage({
                id: "jsm.templates.resources.gettingStartedGuideDescription",
                defaultMessage:
                    "Everything you need to know about getting started with Jira Service Management."
            }),
            link: {
                text: intl.formatMessage({
                    id: "jsm.templates.resources.gettingStartedGuideLink",
                    defaultMessage: "Go to guide"
                }),
                href: useLocalizedHref(
                    "/software/jira/service-management/product-guide/getting-started/quick-start-guide"
                ),
                type: "primary"
            }
        },
        {
            heading: intl.formatMessage({
                id: "jsm.templates.resources.productGuide",
                defaultMessage: "Jira Service Management product guides"
            }),
            description: intl.formatMessage({
                id: "jsm.templates.resources.productGuideDescription",
                defaultMessage:
                    "How to use Jira Service Management: guides, tutorials, and demos."
            }),
            link: {
                text: intl.formatMessage({
                    id: "jsm.templates.resources.productGuideLink",
                    defaultMessage: "Review the guide"
                }),
                href: useLocalizedHref(
                    "/software/jira/service-management/product-guide"
                ),
                type: "primary"
            }
        },
        {
            heading: intl.formatMessage({
                id: "jsm.templates.resources.demo",
                defaultMessage: "Jira Service Management demo"
            }),
            description: intl.formatMessage({
                id: "jsm.templates.resources.demoDescription",
                defaultMessage:
                    "Choose specific features you’re interested in and we’ll personalize a demo for you."
            }),
            link: {
                text: intl.formatMessage({
                    id: "jsm.templates.resources.demoLink",
                    defaultMessage: "Watch the demo"
                }),
                href: useLocalizedHref(
                    "/software/jira/service-management/demo"
                ),
                type: "primary"
            }
        }
    ];

    return (
        <div css={backgroundStyles}>
            <h2 css={h2Styles}>
                <FormattedMessage
                    id="resources.heading"
                    defaultMessage={"Service management resources"}
                />
            </h2>
            <Row layout="4-4-4">
                {resources.map((resource, index) => (
                    <div key={index}>
                        <h4 css={h4Styles}>{resource.heading}</h4>
                        <p css={pStyles}>{resource.description}</p>
                        <Link css={linkStyles} {...resource.link} />
                    </div>
                ))}
            </Row>
        </div>
    );
};
